/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HOST, PORT } from '../environment';
import { getIpBlocked, setIpBlocked, subscribeIpBlocked, parseIpBlockedFromResponse } from '../ipBlockState';
import styles from './BackendConnectionGuard.module.css';

const BACKEND_CHECK_TIMEOUT_MS = 10000;
const BACKEND_CHECK_URL = `${HOST}:${PORT}/api/device/v2/getBloodyLitersSaved`;
const BACKEND_REACHABLE_CACHE_KEY = 'km220.backendReachableAt';
const BACKEND_REACHABLE_CACHE_TTL_MS = 60_000;

/**
 * Pings the backend. Resolves with { ok, ipBlocked, ip }.
 * Any HTTP response (including 429) means the backend/gateway is reachable.
 */
async function checkBackendReachable() {
  try {
    const cachedAt = Number(sessionStorage.getItem(BACKEND_REACHABLE_CACHE_KEY));
    if (Number.isFinite(cachedAt) && Date.now() - cachedAt < BACKEND_REACHABLE_CACHE_TTL_MS) {
      return { ok: true, ipBlocked: false, ip: null };
    }
  } catch (_) {
    // sessionStorage may be unavailable
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), BACKEND_CHECK_TIMEOUT_MS);

  try {
    const response = await fetch(BACKEND_CHECK_URL, {
      method: 'GET',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(timeoutId);

    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      // 429 and some errors return plain text
    }

    const ip = parseIpBlockedFromResponse(response.status, data);
    if (ip !== null) {
      setIpBlocked(ip);
      return { ok: false, ipBlocked: true, ip };
    }

    try {
      sessionStorage.setItem(BACKEND_REACHABLE_CACHE_KEY, String(Date.now()));
    } catch (_) {
      // ignore
    }

    return { ok: true, ipBlocked: false, ip: null };
  } catch (_) {
    clearTimeout(timeoutId);
    return { ok: false, ipBlocked: false, ip: null };
  }
}

/**
 * If the backend does not respond within 10 seconds (or request is blocked, e.g. by provider),
 * shows a full-page error with i18n message suggesting to try from phone.
 */
export default function BackendConnectionGuard({ children }) {
  const { t } = useTranslation();
  const [backendReady, setBackendReady] = useState(false);
  const [showError, setShowError] = useState(false);
  const [blockedIp, setBlockedIp] = useState(getIpBlocked());

  const runCheck = useCallback(() => {
    setShowError(false);
    setBlockedIp(null);

    const timeoutId = setTimeout(() => {
      setShowError(true);
    }, BACKEND_CHECK_TIMEOUT_MS);

    checkBackendReachable().then(result => {
      clearTimeout(timeoutId);
      if (result.ok) {
        setBackendReady(true);
        setShowError(false);
        setBlockedIp(null);
      } else if (result.ipBlocked) {
        setBlockedIp(result.ip || getIpBlocked());
        setShowError(true);
      } else {
        setShowError(true);
      }
    });
  }, []);

  useEffect(() => {
    if (!backendReady) {
      runCheck();
    }
  }, [backendReady, runCheck]);

  useEffect(() => {
    return subscribeIpBlocked(ip => {
      if (ip) {
        setBlockedIp(ip);
        setShowError(true);
        setBackendReady(false);
      }
    });
  }, []);

  if (backendReady && !blockedIp) {
    return children;
  }

  if (showError) {
    if (blockedIp) {
      return (
        <div className={styles.screen}>
          <h1 className={styles.message}>{t('ipBlockedError.title')}</h1>
          <p className={styles.message}>{t('ipBlockedError.message', { ip: blockedIp })}</p>
          <button type="button" onClick={runCheck} className={styles.refreshBtn}>
            {t('refresh')}
          </button>
        </div>
      );
    }
    return (
      <div className={styles.screen}>
        <p className={styles.message}>{t('serverConnectionError')}</p>
        <button type="button" onClick={runCheck} className={styles.refreshBtn}>
          {t('refresh')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <div className={styles.checking}>{t('serverConnectionChecking')}</div>
    </div>
  );
}
